/**
 * Confirmação da cidade antes de criar a rota (módulo puro, sem IA, sem rede
 * e sem custo). Só ignoramos a confirmação quando o destino foi mesmo
 * escolhido pelo utilizador: no campo/autocomplete (`user_selected`) ou
 * nomeado no texto do pedido (`prompt_named`).
 *
 * Uma cidade vinda de "Usar a minha localização" (mesmo precisa), de IP ou de
 * cache NUNCA conta como destino explícito.
 */

/** Origem do destino atualmente ativo. */
export type DestinationSource =
  | "user_selected"
  | "prompt_named"
  | "geolocation_precise"
  | "geolocation_approximate"
  | "ip_fallback"
  | "cache"
  | "none";

/** Origens que dispensam confirmação — o utilizador disse mesmo onde quer. */
export const EXPLICIT_DESTINATION_SOURCES: DestinationSource[] = [
  "user_selected",
  "prompt_named",
];

export function isExplicitDestination(source: DestinationSource): boolean {
  return EXPLICIT_DESTINATION_SOURCES.includes(source);
}

export type PreCreateContext = {
  /** De onde veio a cidade ativa. */
  source: DestinationSource;
  /** Cidade atualmente ativa. */
  city: string;
  /** Chave (cidade+coordenadas+origem) já confirmada nesta sessão. */
  confirmedKey?: string | null;
  /** Chave atual — se diferir da confirmada, voltamos a perguntar. */
  currentKey?: string | null;
};

export type PreCreateConfirmation = {
  kind: "precise" | "approximate";
  message: string;
  confirmLabel: string;
  changeLabel: string;
  hint: string;
};

/** Identifica cidade + coordenadas + origem; muda → confirmação invalidada. */
export function destinationConfirmationKey(input: {
  city: string;
  lat?: number | null;
  lng?: number | null;
  source: DestinationSource;
}): string {
  const round = (n: number | null | undefined) =>
    typeof n === "number" && Number.isFinite(n) ? n.toFixed(3) : "-";
  return [
    input.source,
    (input.city || "").trim().toLowerCase(),
    round(input.lat),
    round(input.lng),
  ].join("|");
}

export function needsCityConfirmation(ctx: PreCreateContext): boolean {
  if (isExplicitDestination(ctx.source)) return false;
  if (!ctx.city || !ctx.city.trim()) return false;
  if (
    ctx.confirmedKey &&
    ctx.currentKey &&
    ctx.confirmedKey === ctx.currentKey
  ) {
    return false;
  }
  return true;
}

type Copy = {
  precise: (city: string) => string;
  approximate: (city: string) => string;
  confirm: (city: string) => string;
  change: string;
  hint: string;
};

const COPY: Record<string, Copy> = {
  pt: {
    precise: (city) => `Queres explorar ${city}?`,
    approximate: (city) =>
      `A tua localização parece ser ${city}. É aqui que queres explorar?`,
    confirm: (city) => `Sim, criar em ${city}`,
    change: "Escolher outra cidade",
    hint: "Ainda não usámos créditos — só queremos acertar no destino.",
  },
  en: {
    precise: (city) => `Do you want to explore ${city}?`,
    approximate: (city) =>
      `Your location looks like ${city}. Is that where you want to explore?`,
    confirm: (city) => `Yes, use ${city}`,
    change: "Pick another city",
    hint: "No credits used yet — we just want the right destination.",
  },
  es: {
    precise: (city) => `¿Quieres explorar ${city}?`,
    approximate: (city) =>
      `Tu ubicación parece ser ${city}. ¿Es aquí donde quieres explorar?`,
    confirm: (city) => `Sí, usar ${city}`,
    change: "Elegir otra ciudad",
    hint: "Aún no usamos créditos — solo queremos acertar el destino.",
  },
  fr: {
    precise: (city) => `Veux-tu explorer ${city} ?`,
    approximate: (city) =>
      `Ta position semble être ${city}. C'est bien là que tu veux explorer ?`,
    confirm: (city) => `Oui, utiliser ${city}`,
    change: "Choisir une autre ville",
    hint: "Aucun crédit utilisé — nous voulons juste la bonne destination.",
  },
  de: {
    precise: (city) => `Möchtest du ${city} erkunden?`,
    approximate: (city) =>
      `Dein Standort scheint ${city} zu sein. Möchtest du hier unterwegs sein?`,
    confirm: (city) => `Ja, ${city} verwenden`,
    change: "Andere Stadt wählen",
    hint: "Noch kein Guthaben verbraucht — wir wollen nur das richtige Ziel.",
  },
  ja: {
    precise: (city) => `${city} を探索しますか？`,
    approximate: (city) =>
      `現在地は ${city} のようです。ここを探索しますか？`,
    confirm: (city) => `はい、${city} で作成`,
    change: "別の都市を選ぶ",
    hint: "クレジットはまだ使っていません。目的地の確認だけです。",
  },
};

export function cityConfirmationCopy(
  city: string,
  language = "pt",
  kind: "precise" | "approximate" = "precise",
): PreCreateConfirmation {
  const copy = COPY[language] ?? COPY.en;
  const name = city.trim();
  return {
    kind,
    message: kind === "approximate" ? copy.approximate(name) : copy.precise(name),
    confirmLabel: copy.confirm(name),
    changeLabel: copy.change,
    hint: copy.hint,
  };
}

/** Devolve a confirmação a mostrar, ou `null` quando podemos criar já. */
export function resolvePreCreateConfirmation(
  ctx: PreCreateContext,
  language = "pt",
): PreCreateConfirmation | null {
  if (!needsCityConfirmation(ctx)) return null;
  const kind =
    ctx.source === "geolocation_precise" ? "precise" : "approximate";
  return cityConfirmationCopy(ctx.city, language, kind);
}

// ---------------------------------------------------------------------------
// Localidade explícita: nunca voltar a perguntar quando já foi dita
// ---------------------------------------------------------------------------

import { extractExplicitLocality } from "./geo-identity";
import { extractTripRequirements, validateHandoffAgainstRequirements } from "./trip-requirements";
import type { PlanHandoff } from "./plan-handoff";

/**
 * Quando o texto já contém "Estou em Vidago" (ou equivalente noutra língua),
 * a origem passa a `prompt_named` e `needsCityConfirmation` deixa de pedir
 * confirmação — sem substituir a localidade pelo município.
 */
export function destinationSourceFromPrompt(
  prompt: string,
  fallback: DestinationSource,
): DestinationSource {
  return extractExplicitLocality(prompt) ? "prompt_named" : fallback;
}

// ---------------------------------------------------------------------------
// Validação do handoff Premium → Pro antes de reservar créditos
// ---------------------------------------------------------------------------

export type HandoffReservationCheck = {
  valid: boolean;
  conflicts: Array<{ code: string; message: string }>;
};

/**
 * Corre a validação do contrato ANTES de `reserveProUsage`. Se houver
 * conflito entre o formulário estruturado e o texto original, devolvemos os
 * conflitos e NÃO consumimos créditos.
 */
export function validateHandoffBeforeReservation(handoff: PlanHandoff): HandoffReservationCheck {
  const requirements = extractTripRequirements(handoff.prompt, { scopeText: handoff.scopeName });
  return validateHandoffAgainstRequirements(requirements, {
    requestedDays: handoff.days,
    cities: handoff.cities,
    transport: handoff.transport,
  });
}
